import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Chip,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import api from "../../../services/api";

const StoresManagement = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
    businessHours: "",
  });

  useEffect(() => {
    fetchStores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStores = async () => {
    try {
      setLoading(true);
      const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
      const companyId = userInfo.companyId;

      if (!companyId) {
        console.error("会社IDが見つかりません");
        setLoading(false);
        return;
      }

      const response = await api.get(`/api/companies/${companyId}/stores`);
      setStores(response.data);
    } catch (error) {
      console.error("店舗情報の取得に失敗しました:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (store = null) => {
    if (store) {
      setEditingStore(store);
      setFormData({
        name: store.name,
        address: store.address,
        phone: store.phone,
        email: store.email,
        businessHours: store.businessHours,
      });
    } else {
      setEditingStore(null);
      setFormData({
        name: "",
        address: "",
        phone: "",
        email: "",
        businessHours: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingStore(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
      const companyId = userInfo.companyId;

      if (!companyId) {
        console.error("会社IDが見つかりません");
        return;
      }

      if (editingStore) {
        await api.put(
          `/api/companies/${companyId}/stores/${editingStore._id}`,
          formData
        );
      } else {
        await api.post(`/api/companies/${companyId}/stores`, formData);
      }
      fetchStores();
      handleCloseDialog();
    } catch (error) {
      console.error("店舗情報の保存に失敗しました:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (storeId) => {
    if (window.confirm("この店舗を削除してもよろしいですか？")) {
      try {
        setDeletingId(storeId);
        const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
        const companyId = userInfo.companyId;

        if (!companyId) {
          console.error("会社IDが見つかりません");
          return;
        }

        await api.delete(`/api/companies/${companyId}/stores/${storeId}`);
        fetchStores();
      } catch (error) {
        console.error("店舗の削除に失敗しました:", error);
      } finally {
        setDeletingId(null);
      }
    }
  };

  return (
    <Container
      maxWidth="lg"
      sx={{ mt: 4, mb: 4 }}
      aria-busy={loading || saving}
    >
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid item xs>
          <Typography variant="h5" component="h1">
            店舗管理
          </Typography>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            disabled={loading}
          >
            新規店舗登録
          </Button>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>店舗名</TableCell>
              <TableCell>住所</TableCell>
              <TableCell>電話番号</TableCell>
              <TableCell>メールアドレス</TableCell>
              <TableCell>営業時間</TableCell>
              <TableCell>ステータス</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : stores.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  店舗が登録されていません
                </TableCell>
              </TableRow>
            ) : (
              stores.map((store) => (
                <TableRow key={store._id}>
                  <TableCell>{store.name}</TableCell>
                  <TableCell>{store.address}</TableCell>
                  <TableCell>{store.phone}</TableCell>
                  <TableCell>{store.email}</TableCell>
                  <TableCell>{store.businessHours}</TableCell>
                  <TableCell>
                    <Chip
                      label={store.status === "ACTIVE" ? "営業中" : "休業中"}
                      color={store.status === "ACTIVE" ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(store)}
                      disabled={Boolean(deletingId)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(store._id)}
                      disabled={deletingId === store._id}
                    >
                      {deletingId === store._id ? (
                        <CircularProgress size={16} />
                      ) : (
                        <DeleteIcon />
                      )}
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>
          {editingStore ? "店舗情報編集" : "新規店舗登録"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label="店舗名"
                fullWidth
                value={formData.name}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="address"
                label="住所"
                fullWidth
                value={formData.address}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="phone"
                label="電話番号"
                fullWidth
                value={formData.phone}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="email"
                label="メールアドレス"
                fullWidth
                value={formData.email}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="businessHours"
                label="営業時間"
                fullWidth
                value={formData.businessHours}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button onClick={handleSubmit} color="primary">
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default StoresManagement;
